import React, { useState } from 'react';
import { createEnsayo } from '../services/ensayoService';
import { Button, FormField, Input, Select } from './ui';

// Versión mínima de EnsayoForm: solo los campos básicos del ensayo
// (sin las secciones de regulado/controlado), enviados a través del
// servicio de la API en lugar de un <form action> crudo apuntando al backend.
const EnsayoFormBasico: React.FC = () => {
  const [formData, setFormData] = useState({
    analito: '',
    medio: 1,
    tipo: '',
    unidad: '',
    estado: 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'medio' || name === 'estado' ? parseInt(value) || 0 : value
    }));
  };

  const handleReset = () => {
    setFormData({ analito: '', medio: 1, tipo: '', unidad: '', estado: 1 });
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await createEnsayo(formData);
      setSuccess('Ensayo creado exitosamente.');
      handleReset();
    } catch (err: any) {
      console.error('Error al crear ensayo:', err);
      setError(err.response?.data?.message || 'Error al crear el ensayo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-semibold mb-6 text-center text-slate-900">Formulario Básico de Ensayos</h2>

      <div className="bg-white p-6 border border-slate-200 rounded-lg shadow-card">
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Analito" htmlFor="analito" required>
            <Input
              type="text"
              id="analito"
              name="analito"
              value={formData.analito}
              onChange={handleChange}
              placeholder="Nombre del analito"
              required
            />
          </FormField>

          <FormField label="Medio (ID)" htmlFor="medio" required>
            <Input
              type="number"
              id="medio"
              name="medio"
              value={formData.medio}
              onChange={handleChange}
              placeholder="Identificador del medio"
              required
            />
          </FormField>

          <FormField label="Tipo" htmlFor="tipo" required>
            <Input
              type="text"
              id="tipo"
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              placeholder="Tipo de ensayo"
              required
            />
          </FormField>

          <FormField label="Unidad" htmlFor="unidad" required>
            <Input
              type="text"
              id="unidad"
              name="unidad"
              value={formData.unidad}
              onChange={handleChange}
              placeholder="Unidad de medida"
              required
            />
          </FormField>

          <FormField label="Estado" htmlFor="estado">
            <Select id="estado" name="estado" value={formData.estado} onChange={handleChange}>
              <option value={1}>Activo</option>
              <option value={0}>Inactivo</option>
            </Select>
          </FormField>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-700">
              {success}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <Button type="button" variant="secondary" onClick={handleReset} disabled={loading}>
              Limpiar
            </Button>
            <Button type="submit" loading={loading}>
              Enviar Ensayo
            </Button>
          </div>
        </form>
      </div>

      <div className="mt-8 p-4 bg-brand-50 border border-brand-100 rounded-md text-brand-800 text-sm">
        <h3 className="font-semibold mb-2">Instrucciones:</h3>
        <ol className="list-decimal list-inside space-y-1">
          <li>Complete todos los campos marcados con asterisco (*)</li>
          <li>El analito debe describir la sustancia o parámetro analizado</li>
          <li>El medio corresponde al identificador del medio de análisis existente</li>
          <li>El tipo especifica la categoría del ensayo</li>
          <li>La unidad indica cómo se expresa el resultado (ej. mg/L)</li>
        </ol>
      </div>
    </div>
  );
};

export default EnsayoFormBasico;
